import { Box, Button, Divider, Typography } from "@mui/material";
import { useHttp } from "../../hooks/useHttp";
import { useEffect, useState } from "react";
import { Pipeline, PipelineSteps, Task, TaskStatus, TaskStatusType } from "../../models/tasks";
import { useLocation } from "react-router";
import { TaskItem } from "../../components/TaskItem/TaskItem";
import { TaskComponent } from "../../components/TaskComponent/TaskComponent";
import { CreateTaskModal } from "../../components/CreateTaskModal/CreateTaskModal";
import { Stats } from "../../models/stats";
import AddIcon from "@mui/icons-material/Add";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { HelpType } from "../../components/Help/Help";

interface TasksInterface {
  authenticated: boolean;
  setHelp: React.Dispatch<React.SetStateAction<HelpType>>;
}

export type StatsMap = {
  [id: string]: Stats;
};

export enum LayoutMode {
  DEFAULT,
  FOLDED_SIDEBAR,
  MOVE_IMAGES,
}

export const AllTasks = ({ authenticated, setHelp }: TasksInterface) => {
  const location = useLocation();

  const {
    loading: fetched_tasks_loading,
    error: fetched_tasks_error,
    trigger: fetchTasks,
  } = useHttp<Task[]>("/tasks/all");

  const { trigger: refreshTaskTrigger } = useHttp("/lungsTasks/task");

  const { trigger: fetchPipelineStats } = useHttp<StatsMap>("/stats");

  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskListener, setTaskListener] = useState<EventSource | null>(null);
  const [preselectedImage, setPreselectedImage] = useState<string | undefined>(undefined);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState<boolean>(false);
  const [pipelineStats, setPipelineStats] = useState<StatsMap | null>(null);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(
    window.innerWidth > 1300
      ? LayoutMode.DEFAULT
      : window.innerWidth > 1000
        ? LayoutMode.FOLDED_SIDEBAR
        : LayoutMode.MOVE_IMAGES,
  );

  useEffect(() => {
    const updateMobile = () => {
      if (window.innerWidth > 1300) setLayoutMode(LayoutMode.DEFAULT);
      else if (window.innerWidth > 1000) setLayoutMode(LayoutMode.FOLDED_SIDEBAR);
      else setLayoutMode(LayoutMode.MOVE_IMAGES);
    };

    window.addEventListener("resize", updateMobile);

    return () => window.removeEventListener("resize", updateMobile);
  }, []);

  const [taskViewOpen, setTaskViewOpen] = useState<boolean>(!layoutMode);

  useEffect(() => {
    if (!layoutMode) setTaskViewOpen(true);
    if (layoutMode && selectedTask) {
      setTaskViewOpen(false);
    }
  }, [layoutMode]);

  const refreshTask = async (task: Task) => {
    if (!taskListener) {
      console.log(`Listener for session is not present. Recreating...`);
      createListener();
    } else {
      console.log(`Listener for session is present`);
    }
    const response: Task = await refreshTaskTrigger({
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        id: task.id,
      },
    });

    if (response) {
      const newTasks = tasks.map((task) => {
        if (task.id === response.id) {
          return response;
        }
        return task;
      });
      setTasks(newTasks);
    }
  };

  useEffect(() => {
    const cachedStats = sessionStorage.getItem("pipelineStats");
    if (cachedStats && cachedStats !== "{}") {
      setPipelineStats(JSON.parse(cachedStats));
    } else {
      fetchPipelineStats().then((response: StatsMap) => {
        setPipelineStats(response);
        sessionStorage.setItem("pipelineStats", JSON.stringify(response));
      });
    }
  }, [fetchPipelineStats]);

  useEffect(() => {
    fetchTasks().then((r: Task[]) => {
      setTasks(r);
    });
  }, []);

  const createListener = () => {
    const sessionId = sessionStorage.getItem("sessionId");
    const url = process.env.REACT_APP_API_URL + "/tasks/updates/" + sessionId;
    let retryCount = 0;
    let retryDelay = 3000;
    const maxRetries = 5;

    const connect = () => {
      console.log(`Attempting to connect... (Retry: ${retryCount})`);
      const eventSource = new EventSource(url);
      setTaskListener(eventSource);

      eventSource.onmessage = (event: MessageEvent) => {
        if (event.data.includes("KEEP_ALIVE")) {
          console.log("Keep-alive");
          return;
        }
        const event_str: string[] = event.data.split("<SEP>");
        const task_id: string = event_str[1];
        const status: TaskStatus = {
          id: event_str[0].slice(6),
          status: event_str[2] as TaskStatusType,
          worker: event_str[3],
          message: event_str[4],
          createdAt: event_str[5],
          processingTime: event_str[6] as unknown as number,
          stage: event_str[7] as PipelineSteps,
          pipeline: event_str[9] as Pipeline,
        };

        setTasks((prevState) => {
          return prevState.map((task) => {
            if (task.id === task_id) {
              if (!task.statuses.find((s) => s.id === status.id)) task.statuses.push(status);
            }
            return task;
          });
        });

        retryCount = 0;
      };

      eventSource.onerror = (error: Event) => {
        console.error("EventSource error:", error);
        eventSource.close();
        setTaskListener(null);

        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`Retrying connection in ${retryDelay / 1000} seconds...`);
          setTimeout(connect, retryDelay);
        } else {
          console.error("Max retries reached. Listener not reconnected.");
        }
      };
    };

    connect();
  };

  useEffect(() => {
    if (!taskListener) createListener();
  }, [tasks]);

  useEffect(() => {
    const updateSelectedTask = () => {
      const params = new URLSearchParams(location.search);
      const taskId = params.get("id");
      if (taskId && tasks && tasks.length > 0) {
        const task = tasks.find((task) => task.id === taskId);
        if (task) {
          setSelectedTask(task);
        } else {
          setSelectedTask(null);
        }
      } else {
        setTaskViewOpen(true);
      }
    };
    updateSelectedTask();

    window.addEventListener("popstate", () => updateSelectedTask());
    return () => window.removeEventListener("popstate", () => updateSelectedTask());
  }, [tasks, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const create = params.get("create");
    if (create !== null) {
      setCreateTaskModalOpen(true);
      const imageId = params.get("create");
      setPreselectedImage(imageId ? imageId : undefined);
    }
  }, [location.search]);

  return (
    <div className={"page tasks"}>
      {createTaskModalOpen && (
        <CreateTaskModal
          open={createTaskModalOpen}
          handleClose={() => {
            setCreateTaskModalOpen(false);
            const params = new URLSearchParams(location.search);
            params.delete("create");
            const newUrl = `${location.pathname}?${params.toString()}`;
            window.history.replaceState(null, "", newUrl);
          }}
          setTasks={setTasks}
          preselectedImageId={preselectedImage}
          setHelp={setHelp}
          isBatch={false}
        />
      )}
      <Box className={layoutMode !== LayoutMode.DEFAULT ? "tasks-body tasks-body-mobile" : "tasks-body"}>
        {taskViewOpen && layoutMode > LayoutMode.DEFAULT && (
          <div className="tasks-list-shadow" onClick={() => setTaskViewOpen(false)} />
        )}
        <div className={taskViewOpen ? "tasks-list" : "tasks-list tasks-list-collapsed"}>
          {layoutMode !== LayoutMode.DEFAULT && (
            <div className="tasks-list-header tasks-list-header-sidebar">
              {!taskViewOpen && <KeyboardDoubleArrowRightIcon onClick={() => setTaskViewOpen(true)} />}
              {taskViewOpen && <KeyboardDoubleArrowLeftIcon onClick={() => setTaskViewOpen(false)} />}
            </div>
          )}
          {layoutMode !== LayoutMode.DEFAULT && <Divider flexItem />}
          <div className="tasks-list-header">
            {taskViewOpen && (
              <Typography variant={"h2"} className="tasks-header-title">
                Tasks
              </Typography>
            )}
            <AddIcon onClick={() => setCreateTaskModalOpen(true)} />
          </div>

          {fetched_tasks_loading && <Typography variant={"h2"}>Loading...</Typography>}
          {fetched_tasks_error && <Typography variant={"h2"}>Error: {fetched_tasks_error.message}</Typography>}
          {taskViewOpen &&
            tasks &&
            tasks.length !== 0 &&
            tasks
              .slice()
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .map((task, index: number) => (
                <div key={index} style={{ width: "100%" }}>
                  {index !== 0 && <Divider flexItem />}
                  <TaskItem
                    task={task}
                    selected={selectedTask?.id === task.id}
                    key={task.id}
                    open={taskViewOpen}
                    reroute="/tasksAdmin?id="
                  />
                </div>
              ))}
          {tasks && tasks.length === 0 && (
            <Box className={"tasks-noTasksFound"}>
              <Typography variant={"h3"}>No tasks found for current session</Typography>
              <Button onClick={() => setCreateTaskModalOpen(true)}>Create first task</Button>
            </Box>
          )}
        </div>
        <Divider orientation={"vertical"} flexItem />
        <Box
          className={"tasks-taskComponentWrapper"}
          style={layoutMode !== LayoutMode.DEFAULT ? { marginLeft: "4rem" } : {}}
        >
          {selectedTask && (
            <TaskComponent
              task={selectedTask}
              refresh={() => refreshTask(selectedTask)}
              pipelineStats={pipelineStats}
              layoutMode={layoutMode}
            />
          )}
        </Box>
      </Box>
    </div>
  );
};
